import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { NAV_ITEMS, type NavItem, type NavSubItem } from '@/lib/constants/navigation';

function CollapsedDropdownItem({
  item,
  visibleSubItems,
  isParentActive,
  isActive,
  getItemUrl,
  t,
}: {
  item: NavItem;
  visibleSubItems: NavSubItem[];
  isParentActive: boolean;
  isActive: (item: NavItem | NavSubItem) => boolean;
  getItemUrl: (item: NavItem | NavSubItem) => string;
  t: (key: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>(null);

  const handleMouseEnter = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  return (
    <SidebarMenuItem
      className="mt-1.5"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DropdownMenu open={open} modal={false}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={open ? undefined : t(item.titleKey)}
            isActive={isParentActive}
            className={
              isParentActive
                ? 'bg-button text-white hover:bg-button hover:text-white'
                : 'hover:bg-button hover:text-white'
            }
          >
            <item.icon className="h-5 w-5" />
            <span>{t(item.titleKey)}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={4}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <DropdownMenuItem asChild>
            <Link to={getItemUrl(item)} className="font-medium">
              {t(item.titleKey)}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {visibleSubItems.map((subItem) => (
            <DropdownMenuItem key={subItem.url} asChild>
              <Link
                to={getItemUrl(subItem)}
                className={
                  isActive(subItem)
                    ? 'bg-button text-white focus:bg-button focus:text-white'
                    : ''
                }
              >
                {t(subItem.titleKey)}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

export function NavMain() {
  const { t } = useTranslation();
  const location = useLocation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const user = useAuthStore((state) => state.user);
  const { state: sidebarState } = useSidebar();
  const isCollapsed = sidebarState === 'collapsed';

  // Filter sub-items based on permissions and role type
  const filterSubItems = (items?: NavSubItem[]): NavSubItem[] => {
    if (!items) return [];
    return items.filter((subItem) => {
      // Check role type restriction
      if (subItem.roleType && user?.role?.type !== subItem.roleType) {
        return false;
      }
      if (subItem.excludeRoleType && user?.role?.type === subItem.excludeRoleType) {
        return false;
      }
      if (subItem.resource && subItem.action) {
        return hasAccess(subItem.resource, subItem.action);
      }
      return true;
    });
  };

  // Filter navigation items based on permissions
  const visibleItems = NAV_ITEMS.filter((item) => {
    // Check single permission if resource and action are specified
    if (item.resource && item.action) {
      if (!hasAccess(item.resource, item.action)) {
        return false;
      }
    }

    // Check any-of permissions — visible if user has at least one
    if (item.permissions && item.permissions.length > 0) {
      if (!item.permissions.some((p) => hasAccess(p.resource, p.action))) {
        return false;
      }
    }

    // Check staff-only restriction
    if (item.staffOnly && user?.role?.name?.toLowerCase() === 'staff') {
      return false;
    }

    // For items with sub-items, hide parent only if it has no own permission and no visible sub-items
    if (item.items && !item.resource) {
      const visibleSubItems = filterSubItems(item.items);
      if (visibleSubItems.length === 0) {
        return false;
      }
    }

    return true;
  });

  const isActive = (item: NavItem | NavSubItem) => {
    return location.pathname === item.url || location.pathname.startsWith(`${item.url}/`);
  };

  const isParentActive = (item: NavItem) => {
    if (isActive(item)) return true;
    if (item.items) {
      return item.items.some((subItem) => isActive(subItem));
    }
    return false;
  };

  // Get URL with query params for specific routes
  const getItemUrl = (item: NavItem | NavSubItem) => {
    if (item.url === '/app/new-tasks' && user?.id) {
      const canSeeAllUsers = user?.role?.type === 'MD' || user?.role?.type === 'MANAGER';
      if (canSeeAllUsers) {
        return `${item.url}?status=NEW`;
      }
      return `${item.url}?status=NEW&assignedStaffId=${user.id}`;
    }
    return item.url;
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {visibleItems.map((item) => {
          const visibleSubItems = filterSubItems(item.items);
          const hasSubItems = visibleSubItems.length > 0;

          if (hasSubItems) {
            // When collapsed, use DropdownMenu on hover
            if (isCollapsed) {
              return (
                <CollapsedDropdownItem
                  key={item.url}
                  item={item}
                  visibleSubItems={visibleSubItems}
                  isParentActive={isParentActive(item)}
                  isActive={isActive}
                  getItemUrl={getItemUrl}
                  t={t}
                />
              );
            }

            // When expanded, always show sub-items
            return (
              <SidebarMenuItem key={item.url} className="mt-1.5">
                <SidebarMenuButton
                  asChild
                  tooltip={t(item.titleKey)}
                  isActive={isParentActive(item)}
                  className={
                    isParentActive(item)
                      ? 'bg-button text-white hover:bg-button hover:text-white'
                      : 'hover:bg-button hover:text-white'
                  }
                >
                  <Link to={getItemUrl(item)}>
                    <item.icon className="h-5 w-5" />
                    <span>{t(item.titleKey)}</span>
                  </Link>
                </SidebarMenuButton>
                <SidebarMenuSub>
                  {visibleSubItems.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.url}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={isActive(subItem)}
                        className={
                          isActive(subItem)
                            ? 'bg-button text-white hover:bg-button hover:text-white'
                            : 'hover:bg-button hover:text-white'
                        }
                      >
                        <Link to={getItemUrl(subItem)}>
                          <span>{t(subItem.titleKey)}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarMenuItem>
            );
          }

          return (
            <SidebarMenuItem key={item.url} className="mt-1.5">
              <Link to={getItemUrl(item)}>
                <SidebarMenuButton
                  isActive={isActive(item)}
                  tooltip={t(item.titleKey)}
                  className={
                    isActive(item)
                      ? 'bg-button text-white hover:bg-button hover:text-white'
                      : 'hover:bg-button hover:text-white'
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span>{t(item.titleKey)}</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
